"use client";

import * as React from "react";
import { cn } from "../../lib/utils";

export interface DesignPreviewJob {
  id: string | number;
  jobTitle: string;
  companyName: string;
  location: string;
  salary: string;
  experience: string;
  workMode: string;
  basePay: string;
  skills: string[];
}

export interface DesignPreviewCardProps {
  className?: string;
  name: string;
  subtitle: string;
  avatar: string;
  jobs: DesignPreviewJob[];
  cardColor: string;
  titleColor: string;
  ctaColor?: string;
  textColor: string;
}

export function DesignPreviewCard({
  className,
  name,
  subtitle,
  avatar,
  jobs,
  cardColor,
  titleColor,
  ctaColor = "#0D41E1",
  textColor,
}: DesignPreviewCardProps) {
  const badgeBg = `${textColor}1A`;

  return (
    <div className={cn("w-full max-w-[380px] mx-auto space-y-4", className)}>
      {/* OUTER WHITE CARD */}
      <div className="rounded-2xl bg-white border border-[#D0D5DD] p-3 shadow-[0_3px_0_rgba(0,0,0,0.1)]">
        {/* BLUE INNER CARD */}
        <div
          className="relative rounded-xl border-[3px] border-black p-4"
          style={{
            backgroundColor: cardColor,
          }}
        >
          {/* Sparkle icon */}
          <SparkleIcon className="absolute top-2 left-2 h-6 w-6" />

          {/* Avatar */}
          <div className="flex flex-col items-center mt-6">
            <div className="h-20 w-20 rounded-full border-[3px] border-black bg-white flex items-center justify-center overflow-hidden absolute -top-10">
              <img src={avatar} className="h-full w-full object-cover" />
            </div>

            <p className="mt-3 text-[18px] font-black uppercase tracking-wide text-black"
            style={{
            color: titleColor,
          }}
            >
              {name}
            </p>
            <p className="text-[12px] text-black opacity-70 text-center leading-snug">
              {subtitle}
            </p>

            {/* STATS */}
            <div className="mt-3 flex items-center gap-2">
              <StatPill value="5.1K" icon="instagram" />
              <StatPill value="5.1K" icon="star" />
              <StatPill value="5.1K" icon="eye" />
            </div>
          </div>
        </div>
      </div>

      {/* JOB LIST */}
      <p className="text-[15px] font-semibold text-black">Jobs ({jobs.length})</p>

      <div className="space-y-3">
        {jobs.map((job) => (
          <JobMiniCard key={job.id} {...job} badgeColor={textColor} badgeBackground={badgeBg} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- JOB CARD ---------------- */

function JobMiniCard({
  jobTitle,
  companyName,
  location,
  salary,
  experience,
  workMode,
  basePay,
  skills,
  badgeColor,
  badgeBackground,
}: DesignPreviewJob & { badgeColor: string; badgeBackground: string }) {
  return (
    <div className="rounded-2xl bg-white border border-[#D0D5DD] p-4 shadow-[0_2px_0_rgba(0,0,0,0.08)]">
      {/* ICON + TITLE */}
      <div className="flex items-center gap-2">
        <RainbowIcon className="h-5 w-5" />
        <p className="text-[15px] font-semibold text-black">{jobTitle}</p>
      </div>

      {/* COMPANY + LOCATION */}
      <div className="mt-1 flex items-center gap-1 text-[12px] text-[#475467]">
        <span className="truncate">{companyName}</span>
        <div className="h-1 w-1 rounded-full bg-[#D0D5DD]" />
        <span>{location}</span>
      </div>

      {/* BADGES ROW */}
      <div className="mt-3 flex flex-wrap gap-2">
        <JobBadge label={experience} color={badgeColor} bg={badgeBackground} />
        <JobBadge label={workMode} color={badgeColor} bg={badgeBackground} />
        <JobBadge label={salary} color={badgeColor} bg={badgeBackground} />
        <JobBadge label={basePay} color={badgeColor} bg={badgeBackground} />
      </div>

      {/* SKILLS */}
      <div className="mt-3 flex flex-wrap gap-2">
        {skills.slice(0, 4).map((skill) => (
          <SkillChip key={skill} label={skill} color={badgeColor} bg={badgeBackground} />
        ))}
        {skills.length > 4 && (
          <SkillChip label={`+${skills.length - 4}`} color={badgeColor} bg={badgeBackground} />
        )}
      </div>

      {/* SUMMARY BAR */}
      <div
        className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-medium"
        style={{ backgroundColor: badgeBackground, color: badgeColor }}
      >
        <LoadingIcon className="h-4 w-4" />
        <span>Generating Summary...</span>
      </div>
    </div>
  );
}

/* -------- BADGES / PILLS -------- */

function StatPill({ value, icon }: { value: string; icon: string }) {
  const icons: Record<string, React.ReactNode> = {
    instagram: <InstagramIcon className="h-3 w-3" />,
    star: <StarIcon className="h-3 w-3" />,
    eye: <EyeIcon className="h-3 w-3" />,
  };

  return (
    <div className="flex items-center gap-1 rounded-md border border-black bg-white px-2 py-1 text-[11px] font-semibold shadow-[0_1px_0_rgba(0,0,0,0.2)]">
      {icons[icon]}
      <span>{value}</span>
    </div>
  );
}

function JobBadge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="rounded-md text-[11px] font-medium px-2 py-[3px] border"
      style={{ color, borderColor: color, backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

function SkillChip({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span
      className="rounded-md text-[11px] font-medium px-2 py-[3px] border"
      style={{ color, borderColor: color, backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

/* -------- INLINE ICONS -------- */

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M12 2.5 13.8 9 20 11 13.8 13 12 19.5 10.2 13 4 11 10.2 9 12 2.5Z"
        fill="#0D41E1"
      />
    </svg>
  );
}

function RainbowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="rainbowGrad" x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#FF5F6D" />
          <stop offset="35%" stopColor="#FFC371" />
          <stop offset="70%" stopColor="#4FACFE" />
          <stop offset="100%" stopColor="#00F2FE" />
        </linearGradient>
      </defs>
      <path
        d="M6 18c0-4.418 3.582-8 8-8s8 3.582 8 8h4c0-6.627-5.373-12-12-12S2 11.373 2 18h4Zm4 0a4 4 0 0 1 8 0h4c0-4.418-3.582-8-8-8s-8 3.582-8 8h4Zm4 0a1 1 0 0 1 2 0h4c0-3.314-2.686-6-6-6s-6 2.686-6 6h4Z"
        fill="url(#rainbowGrad)"
      />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="instaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F58529" />
          <stop offset="50%" stopColor="#DD2A7B" />
          <stop offset="100%" stopColor="#8134AF" />
        </linearGradient>
      </defs>
      <path
        d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm5 6.5A3.5 3.5 0 1 0 15.5 12 3.5 3.5 0 0 0 12 8.5Zm5.25-2.625a1.125 1.125 0 1 0 1.125 1.125 1.125 1.125 0 0 0-1.125-1.125Z"
        fill="url(#instaGrad)"
      />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
      <path
        d="M12 3.5 14.35 8.9l5.15.41-3.93 3.37 1.2 5.02L12 15.9l-4.77 1.8 1.2-5.02-3.93-3.37 5.15-.41L12 3.5Z"
        fill="#FFC24C"
      />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
      <path
        d="M12 6c4.5 0 8.5 2.95 10 6-1.5 3.05-5.5 6-10 6S3.5 15.05 2 12c1.5-3.05 5.5-6 10-6Z"
        stroke="#0D41E1"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="12" r="3" fill="#0D41E1" />
    </svg>
  );
}

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
